import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LocationService } from '../services/location.service';
import { SessionStorageService } from '../services/session-storage.service';
import { AppService } from '../services/app.service';
import { Location, Company } from '../interfaces/location.model';
import { MAP_CONFIG } from '../constants/map.constants';

@Component({
  selector: 'app-dealer-location',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule, MatSlideToggleModule],
  templateUrl: './dealer-location.component.html',
  styleUrls: ['./dealer-location.component.scss']
})
export class DealerLocationComponent implements OnInit, OnDestroy {
  locations: Location[] = [];
  selectedDealer: Location | null = null;
  showMapView = false;
  private unsubscribe$ = new Subject<void>();
  private map: any = null;
  private markers: any[] = [];
  private L: any;
  loader: boolean = false;
  showNoResults: boolean = false;
  isComponentReady: boolean = false;

  constructor(
    private locationService: LocationService,
    private readonly router: Router,
    private readonly sessionStorageService: SessionStorageService,
    private readonly appService: AppService,
    @Inject(PLATFORM_ID) private readonly platformId: Object
  ) {}

  ngOnInit(): void {
    this.appService.loader$.subscribe((data: any) => {
      this.loader = data;
    });
    this.loadMapViewFromSession();
    this.loadData();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() => {
        if (this.router.url === '/locations') {
          this.loadMapViewFromSession(); 
          this.loadData();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private loadData(): void {
    this.showNoResults = false;
    this.loadSelectedDealerFromSession();
    this.locationService.getLocations().subscribe((res: Location[]) => {
      this.locations = res;
      if (!this.isComponentReady) {
        Promise.resolve().then(() => {
          setTimeout(() => {
            this.isComponentReady = true;
            this.appService.setComponentReady(true);
          }, 200);
        });
      }
      if (!this.locations || this.locations.length === 0) {
        setTimeout(() => {
          this.showNoResults = true;
        }, 3000);
      }
      if (
        this.showMapView &&
        isPlatformBrowser(this.platformId)
      ) {
        if (this.L) {
          setTimeout(() => this.initializeMap(), 100);
        } else {
          import('leaflet').then(leaflet => {
            this.L = leaflet.default;
            setTimeout(() => this.initializeMap(), 100);
          });
        }
      }
    });
  }

  hasValidLocations(): boolean {
  return !!this.locations?.some(loc => 
    loc.geoLatitude !== null && 
    loc.geoLatitude !== undefined && 
    loc.geoLatitude !== 0 && 
    loc.geoLongitude !== null && 
    loc.geoLongitude !== undefined && 
    loc.geoLongitude !== 0 &&
    Math.abs(loc.geoLatitude) <= 90 &&
    Math.abs(loc.geoLongitude) <= 180
  );
}

hasNoLocations(): boolean {
  return this.showNoResults;
}

  selectDealer(loc: Location): void {
    this.selectedDealer = loc;
    this.updateMapMarkers();
  }

  selectDealerFromTile(loc: Location): void {
    this.selectedDealer = loc;
    this.saveDealerToSession(loc);
    this.updateMapMarkers();
    this.router.navigate(['/book-appointment/vehicle-info']);
  }

  onToggleChange(event: any): void {
    this.showMapView = event.checked;
    this.sessionStorageService.setSessionItem('showMapView', JSON.stringify(this.showMapView));
    const storedValue = this.sessionStorageService.getSessionItem('showMapView');
    this.showMapView = storedValue ? JSON.parse(storedValue) : false;
    if (this.showMapView && isPlatformBrowser(this.platformId)) {
      if (this.L) {
        setTimeout(() => {
          this.initializeMap();
        }, 100);
      } else {
        import('leaflet').then(leaflet => {
          this.L = leaflet.default;
          setTimeout(() => {
            this.initializeMap();
          }, 100);
        });
      }
    } else if (!this.showMapView) {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
    }
  }

  isSelected(loc: Location): boolean {
    return (
      this.selectedDealer?.id === loc.id ||
      this.selectedDealer?.Name === loc.Name
    );
  }

  private loadSelectedDealerFromSession(): void {
    const sessionDataString =
      this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString
      ? JSON.parse(sessionDataString)
      : null;

    if (sessionData?.dealership) {
      this.selectedDealer = sessionData.dealership as Location;
    }
  }

  private saveDealerToSession(dealer: Location): void {
    const sessionDataString =this.sessionStorageService.getSessionItem('oabAppSessionData');
    const sessionData = sessionDataString? JSON.parse(sessionDataString): {};
    const updatedSessionData = {
      ...sessionData,
      dealership: dealer
    };
    this.sessionStorageService.setSessionItem('oabAppSessionData',JSON.stringify(updatedSessionData));

    const companiesListString =this.sessionStorageService.getSessionItem('companiesList');
      if (companiesListString) {
      const companiesList: Company[] = JSON.parse(companiesListString);
      
      companiesList.forEach((company: Company) => {
        (company as Company & { isSelected?: boolean }).isSelected = false;
        company.locations?.forEach(loc => (loc.isSelected = false));
        company.Locations?.forEach(loc => (loc.isSelected = false));
        if (company.id === dealer.company.id) {
          (company as Company & { isSelected?: boolean }).isSelected = true;
          const allLocs = [...(company.locations ?? []), ...(company.Locations ?? [])];
          allLocs.forEach(loc => {
            if (loc.id === dealer.id) {
              loc.isSelected = true;
            }
          });
        }
      });

      this.sessionStorageService.setSessionItem(
        'companiesList',
        JSON.stringify(companiesList)
      );
    }
  }

  private initializeMap(): void {
    if (!this.L) return;

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    const validLocations = this.locations.filter(loc => 
      loc.geoLatitude && loc.geoLongitude
    );

    if (validLocations.length === 0) {
      return;
    }

    setTimeout(() => {
      const mapElement = document.getElementById('dealer-map');
      if (!mapElement) {
        return;
      }

      const rect = mapElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        setTimeout(() => this.initializeMap(), 500);
        return;
      }

      this.map = this.L.map('dealer-map').setView([
        validLocations[0].geoLatitude!,
        validLocations[0].geoLongitude!
      ], MAP_CONFIG.DEFAULT_ZOOM);

      this.L.tileLayer(MAP_CONFIG.TILE_LAYERS.CARTODB_POSITRON.URL, {
        attribution: MAP_CONFIG.TILE_LAYERS.CARTODB_POSITRON.ATTRIBUTION
      }).addTo(this.map);

      const defaultIcon = this.L.icon(MAP_CONFIG.MARKERS.DEFAULT_ICON);
      const selectedIcon = this.L.icon(MAP_CONFIG.MARKERS.SELECTED_ICON);

      validLocations.forEach(location => {
        const marker = this.L.marker([
          location.geoLatitude!,
          location.geoLongitude!
        ]);

        const popupContent = `
          <div>
            <strong>${location.Name}</strong><br>
            ${location.Address}
          </div>
        `;

        marker.bindPopup(popupContent);
        
        if (this.isSelected(location)) {
          marker.setIcon(selectedIcon);
        } else {
          marker.setIcon(defaultIcon);
        }

        marker.addTo(this.map);
        this.markers.push(marker);

        marker.on('click', () => {
          this.selectDealer(location);
        });
      });

      if (this.markers.length > 0) {
        const group = new this.L.FeatureGroup(this.markers);
        this.map.fitBounds(group.getBounds().pad(0.1));
      }

      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      }, 100);
    }, 300);
  }


  onContinueClick(): void {
    if (this.selectedDealer) {
      this.saveDealerToSession(this.selectedDealer);
      this.router.navigate(['/book-appointment/vehicle-info']);
    }
  }

  private updateMapMarkers(): void {
    if (!this.L || !this.map) return;

    const defaultIcon = this.L.icon(MAP_CONFIG.MARKERS.DEFAULT_ICON);
    const selectedIcon = this.L.icon(MAP_CONFIG.MARKERS.SELECTED_ICON);

    const validLocations = this.locations.filter(loc => 
      loc.geoLatitude && loc.geoLongitude
    );

    this.markers.forEach((marker, index) => {
      const location = validLocations[index];
      
      if (location && this.isSelected(location)) {
        marker.setIcon(selectedIcon);
      } else {
        marker.setIcon(defaultIcon);
      }
    });
  }

  private loadMapViewFromSession(): void {
    const storedValue = this.sessionStorageService.getSessionItem('showMapView');
    this.showMapView = storedValue ? JSON.parse(storedValue) : false;
  }

}
